import { useCallback, useEffect, useRef, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useWindowSize } from '@react-hook/window-size'

import { tokenVar, useEscapeKey } from '@hivewatch/dark'

import { getOrderBy } from '@/features/sop-tabs'
import { useStandardOperatingProceduresQuery } from '@/graphql/generated/hooks'
import { SopType } from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeSOPIncidentDrawer,
  selectSOPIncidentDrawerState,
  selectSOPPageSize,
  setSOPIncidentId,
} from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { SOP_INCIDENT_DRAWER_CLOSE } from '../../../shared/constants/analyticsEvents'
import {
  SOP_DRAWER_BREAKPOINT_REBRAND,
  SOP_DRAWER_WIDTH_REBRAND,
} from '../../../shared/constants/drawerDimensions'
import { type IncidentContextI } from '../../../shared/types/incidentContextTypes'
import { IncidentDrawerContainer } from '../../components/IncidentDrawerContainer'
import { SOPIncidentDetail } from './SOPIncidentDetail'
import { SOPIncidentTable } from './SOPIncidentTable'

interface SOPIncidentDrawerIProps {
  incidentContext: IncidentContextI | null
}

export const SOPIncidentDrawer = ({
  incidentContext,
}: SOPIncidentDrawerIProps) => {
  const { id, isOpen, sopNameLike } = useAppSelector(
    selectSOPIncidentDrawerState
  )
  const dispatch = useAppDispatch()
  const mode = useLightMode()
  const isDark = mode === 'dark'
  const [width] = useWindowSize()
  const drawerWidth = SOP_DRAWER_WIDTH_REBRAND
  const drawerBg = isDark ? tokenVar('colors.bg.card') : 'white'
  const initialPageSize = useAppSelector(selectSOPPageSize) ?? 25

  // Lifted from SOPsTable so changes flow through query variables and hit
  // Apollo's cache. Owning the query at the drawer level (above the table)
  // keeps the cache subscription alive when the user opens an SOP detail
  // view, which unmounts the table.
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>(null)

  const facilityId = incidentContext?.facility_id

  const {
    data: sopData,
    loading: isSopLoading,
    refetch: refetchSops,
  } = useStandardOperatingProceduresQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isOpen || !facilityId,
    variables: {
      filter: {
        nameLike: sopNameLike,
        type: SopType.Operator,
        facilityIdIn: facilityId ? [facilityId] : [],
      },
      first: pageIndex,
      last: pageSize,
      orderBy: getOrderBy(sortColumn, sortOrder),
    },
  })

  useEffect(() => {
    setPageIndex(0)
  }, [sopNameLike, facilityId])

  // A detail selected under one incident must not leak into the next — clear
  // the selection when the drawer's incident changes while it stays mounted.
  const previousIncidentIdRef = useRef(incidentContext?.incident_id)
  useEffect(() => {
    const incidentId = incidentContext?.incident_id
    if (previousIncidentIdRef.current !== incidentId) {
      previousIncidentIdRef.current = incidentId
      dispatch(setSOPIncidentId({ id: null }))
    }
  }, [incidentContext?.incident_id, dispatch])

  useEffect(() => {
    if (width < SOP_DRAWER_BREAKPOINT_REBRAND) {
      dispatch(closeSOPIncidentDrawer())
    }
  }, [width])

  // Escape closes this sub-drawer only — the incident drawer behind it stays
  // open. Joins the dark package's escape stack so nesting resolves. Mirrors
  // the header's close button, including its analytics. The ref scopes the
  // foreign-dialog guard: a Chakra v1 dialog opened from the action bar
  // (Emergency Call) traps focus without joining the stack, and must keep
  // Escape rather than have it close this drawer behind it. ENG-32100
  const containerRef = useRef<HTMLDivElement>(null)
  const handleEscape = useCallback(() => {
    trackEvent(SOP_INCIDENT_DRAWER_CLOSE, {
      facility_id: incidentContext?.facility_id,
      incident_id: incidentContext?.incident_id,
      closed_via: 'escape_key',
    })
    dispatch(closeSOPIncidentDrawer())
  }, [dispatch, incidentContext?.facility_id, incidentContext?.incident_id])
  useEscapeKey(handleEscape, Boolean(isOpen), containerRef)

  if (!isOpen) return null

  return (
    <IncidentDrawerContainer
      bgColor={drawerBg}
      borderColor={
        isDark
          ? tokenVar('colors.border.subtle')
          : tokenVar('colors.light.border.subtle')
      }
      containerRef={containerRef}
      dataTestid='SOPIncidentDrawerContainer:wrapper'
      width={drawerWidth}
    >
      {id !== null ? (
        <SOPIncidentDetail id={id} />
      ) : incidentContext ? (
        <SOPIncidentTable
          facilityId={incidentContext.facility_id}
          incidentId={incidentContext.incident_id}
          isSopLoading={isSopLoading}
          pageIndex={pageIndex}
          pageSize={pageSize}
          refetchSops={refetchSops}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          setSortColumn={setSortColumn}
          setSortOrder={setSortOrder}
          sopData={sopData}
          sortColumn={sortColumn}
          sortOrder={sortOrder}
        />
      ) : null}
    </IncidentDrawerContainer>
  )
}
